import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import type { RunnerCategory, TrainingConstants, UserProfile } from '@shared/types';
import React, { useState } from 'react';
import { DEFAULT_USER_PROFILE } from '../../utils/userProfile';
import { ThresholdPaceCalculator } from '../profile/ThresholdPaceCalculator';
import WeeklyWorkoutPreferences from '../profile/WeeklyWorkoutPreferences';

interface OnboardingWizardProps {
  onComplete: (profile: UserProfile) => void;
  onSkip?: () => void;
}

const RUNNER_CATEGORIES: Array<{ value: RunnerCategory; label: string }> = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

const deriveRunnerCategoryFromWeeklyMileage = (weeklyMileage: number): RunnerCategory => {
  if (weeklyMileage < 20) return 'beginner';
  if (weeklyMileage <= 70) return 'intermediate';
  return 'advanced';
};

const mapWeeklyMileageFromRunnerCategory = (category: RunnerCategory): number => {
  switch (category) {
    case 'beginner':
      return 15;
    case 'intermediate':
      return 50;
    case 'advanced':
    default:
      return 80;
  }
};

const getLongRunPercentageForCategory = (category: RunnerCategory): number => {
  switch (category) {
    case 'beginner':
      return 0.45;
    case 'intermediate':
      return 0.3;
    case 'advanced':
    default:
      return 0.25;
  }
};

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete, onSkip }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [profile, setProfile] = useState<Partial<UserProfile>>({
    ...DEFAULT_USER_PROFILE,
    thresholdPaceSec: 240, // Default 4:00/km
    weeklyMileage: 50,
    trainingRatio: {
      running: 100,
      cycling: 0,
    },
    weeklyWorkoutPreferences: {
      Monday: ['rest'],
      Tuesday: ['rest'],
      Wednesday: ['rest'],
      Thursday: ['rest'],
      Friday: ['rest'],
      Saturday: ['rest'],
      Sunday: ['rest'],
    },
  });

  const [thresholdMinutes, setThresholdMinutes] = useState(4);
  const [thresholdSeconds, setThresholdSeconds] = useState(0);
  const [isWeeklyPreferencesValid, setIsWeeklyPreferencesValid] = useState(false);

  const steps = ['Threshold Pace', 'Weekly Target', 'Weekly Workout Preferences'];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // Last step - complete the wizard
      onComplete(profile as UserProfile);
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleThresholdChange = (minutes: number, seconds: number) => {
    setThresholdMinutes(minutes);
    setThresholdSeconds(seconds);
    setProfile(prev => ({
      ...prev,
      thresholdPaceSec: minutes * 60 + seconds,
    }));
  };

  const handleWeeklyMileageChange = (value: number) => {
    const newCategory = deriveRunnerCategoryFromWeeklyMileage(value);
    const newLongRunPercentage = getLongRunPercentageForCategory(newCategory);

    setProfile(prev => ({
      ...prev,
      weeklyMileage: value,
      runnerCategory: newCategory,
      trainingConstants: {
        ...(prev.trainingConstants || {}),
        longRunPercentage: newLongRunPercentage,
      } as TrainingConstants,
    }));
  };

  const handleRunnerCategoryChange = (category: RunnerCategory) => {
    const weeklyMileage = mapWeeklyMileageFromRunnerCategory(category);
    const newLongRunPercentage = getLongRunPercentageForCategory(category);

    setProfile(prev => ({
      ...prev,
      runnerCategory: category,
      weeklyMileage,
      trainingConstants: {
        ...(prev.trainingConstants || {}),
        longRunPercentage: newLongRunPercentage,
      } as TrainingConstants,
    }));
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0: // Threshold pace
        return profile.thresholdPaceSec && profile.thresholdPaceSec > 0;
      case 1: // Weekly mileage
        return profile.weeklyMileage && profile.weeklyMileage > 0;
      case 2: // Weekly workout preferences
        return isWeeklyPreferencesValid;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              What is your threshold pace?
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Your threshold pace is the pace you can sustain for about 60 minutes at maximum
                effort. This is typically your 10K race pace or slightly slower than your half
                marathon pace. If you're unsure, start with a conservative estimate - you can always
                adjust it later.
              </Typography>
            </Alert>

            <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label="Minutes"
                type="number"
                value={thresholdMinutes}
                onChange={e =>
                  handleThresholdChange(parseInt(e.target.value) || 0, thresholdSeconds)
                }
                inputProps={{ min: 3, max: 10 }}
                sx={{ width: 120 }}
                aria-label="Threshold pace minutes"
              />
              <Typography variant="h6">:</Typography>
              <TextField
                label="Seconds"
                type="number"
                value={thresholdSeconds}
                onChange={e =>
                  handleThresholdChange(thresholdMinutes, parseInt(e.target.value) || 0)
                }
                inputProps={{ min: 0, max: 59 }}
                sx={{ width: 120 }}
                aria-label="Threshold pace seconds"
              />
              <Typography variant="h6">/ km</Typography>
              <ThresholdPaceCalculator
                onSave={thresholdPaceSeconds => {
                  const minutes = Math.floor(thresholdPaceSeconds / 60);
                  const seconds = thresholdPaceSeconds % 60;
                  handleThresholdChange(minutes, seconds);
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Example: 4:30/km is 4 minutes and 30 seconds per kilometer
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              What is your weekly training target?
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                This is your target weekly mileage in kilometers. Consider your current fitness
                level and available time. It's better to start conservatively and build up
                gradually. A typical recreational runner might aim for 30-50 km per week, while more
                experienced runners might target 60-100+ km.
              </Typography>
            </Alert>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Runner Category</InputLabel>
              <Select
                value={
                  (profile.runnerCategory as RunnerCategory | undefined) ??
                  deriveRunnerCategoryFromWeeklyMileage(profile.weeklyMileage || 0)
                }
                onChange={e => handleRunnerCategoryChange(e.target.value as RunnerCategory)}
                label="Runner Category"
                aria-label="Select runner category"
              >
                {RUNNER_CATEGORIES.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Beginner: &lt;20km/week • Intermediate: 20–70km/week • Advanced: &gt;70km/week
              </FormHelperText>
            </FormControl>

            <TextField
              fullWidth
              label="Weekly Mileage (km)"
              type="number"
              value={profile.weeklyMileage}
              onChange={e => handleWeeklyMileageChange(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 5, max: 200, step: 5 }}
              helperText="Enter your target weekly distance in kilometers"
              aria-label="Weekly mileage in kilometers"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Set your weekly workout preferences
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Customize your training week by selecting workout types for each day. Choose from
                easy runs/rides, long runs/rides, hard runs/rides, or rest days. If you do both
                running and cycling, adjust the training ratio slider to match your preferred mix.
              </Typography>
            </Alert>

            <WeeklyWorkoutPreferences
              profile={profile as UserProfile}
              onChange={(updates: Partial<UserProfile>) => {
                setProfile(prev => ({ ...prev, ...updates }));
              }}
              onValidationChange={setIsWeeklyPreferencesValid}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img
              src="/icons/PacePilot_icon - 128.png"
              alt="PacePilot"
              style={{ width: 80, height: 80, marginBottom: 16 }}
            />
            <Typography variant="h4" gutterBottom>
              Welcome to PacePilot!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Let's set up your training profile in just a few steps
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mb: 4, minHeight: 300 }}>{renderStepContent()}</Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              aria-label="Go back to previous step"
            >
              Back
            </Button>
            <Box>
              {onSkip && activeStep === 0 && (
                <Button onClick={onSkip} sx={{ mr: 1 }} aria-label="Skip onboarding setup">
                  Skip Setup
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed()}
                aria-label={activeStep === steps.length - 1 ? 'Complete setup' : 'Go to next step'}
              >
                {activeStep === steps.length - 1 ? 'Complete Setup' : 'Next'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OnboardingWizard;
